import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Container, Typography } from '@mui/material';
import { logError } from '~src/utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details for debugging
    logError(`ErrorBoundary caught an error: ${error.message} - Component stack: ${errorInfo.componentStack}`);
    
    // Skip logging certain errors to Sentry
    const errorMessage = error.message || '';
    const isNetworkError = errorMessage.includes('Failed to fetch') || 
                          errorMessage.includes('NetworkError') ||
                          errorMessage.includes('timeout');
    const isDynamicImportError = errorMessage.includes('Failed to fetch dynamically imported module');
    
    // Only log non-network errors to prevent noise
    if (!isNetworkError && !isDynamicImportError) {
      // Error will be sent to Sentry automatically
    }
  }

  clearCachesAndReload = () => {
    // Clear service worker caches to ensure fresh modules are loaded
    if (typeof caches !== 'undefined') {
      caches.keys().then((names) => {
        return Promise.all(names.map((name) => caches.delete(name)));
      }).finally(() => {
        location.reload();
      });
    } else {
      location.reload();
    }
  };

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
    // For dynamic import errors, clear caches before reloading
    if (this.state.error?.message?.includes('dynamically imported module')) {
      this.clearCachesAndReload();
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      const { error } = this.state;
      const isDynamicImportError = error?.message?.includes('dynamically imported module');

      return (
        <Container maxWidth="sm">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              textAlign: 'center',
              gap: 2,
            }}
          >
            <Typography variant="h4" gutterBottom>
              {isDynamicImportError ? 'Loading Error' : 'Something went wrong'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {isDynamicImportError 
                ? 'Failed to load application resources. This may be due to a network issue or an outdated version.'
                : 'An unexpected error occurred. Please try again.'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                variant="contained" 
                onClick={this.handleReset}
              >
                {isDynamicImportError ? 'Reload Page' : 'Try Again'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => window.location.href = '/'}
              >
                Go to Home
              </Button>
            </Box>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}